import { useEffect, useRef, lazy, Suspense } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import { BLOG_POSTS } from "@/lib/blog";

// Lazy-load per-post content components
const WhatIsYachtRock = lazy(() => import("@/pages/blog-posts/WhatIsYachtRock"));
const PlaylistPost = lazy(() => import("@/pages/blog-posts/PlaylistPost"));
const ConcertsPost = lazy(() => import("@/pages/blog-posts/ConcertsPost"));
const FirstTimePost = lazy(() => import("@/pages/blog-posts/FirstTimePost"));
const WaterfrontPost = lazy(() => import("@/pages/blog-posts/WaterfrontPost"));
const BandsPost = lazy(() => import("@/pages/blog-posts/BandsPost"));
const MGHCharityPost = lazy(() => import("@/pages/blog-posts/MGHCharityPost"));

const CONTENT_MAP: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  "what-is-yacht-rock": WhatIsYachtRock,
  "the-ultimate-yacht-rock-playlist": PlaylistPost,
  "best-summer-concerts-in-2026": ConcertsPost,
  "your-first-time-at-yacht-rock-detroit": FirstTimePost,
  "detroit-waterfront-events-2026": WaterfrontPost,
  "15-yacht-rock-bands-you-need-to-know": BandsPost,
  "yacht-rock-detroit-unplugged-charity-gala": MGHCharityPost,
};

/** Generate JSON-LD structured data for a blog post */
const generateJsonLd = (post: typeof BLOG_POSTS[0]) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.metaDescription,
  image: post.image,
  author: {
    "@type": "Organization",
    name: post.author,
    url: "https://yachtrockdetroit.com",
  },
  publisher: {
    "@type": "Organization",
    name: "Yacht Rock Detroit",
    logo: {
      "@type": "ImageObject",
      url: "https://yachtrockdetroit.com/favicon.svg",
    },
  },
  datePublished: post.datePublished,
  dateModified: post.dateModified,
  keywords: post.keywords.join(", "),
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `https://yachtrockdetroit.com/lifestyle/blog/${post.slug}`,
  },
});

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const heroRef = useRef<HTMLElement>(null);
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  // Set document meta tags for SEO
  useEffect(() => {
    if (!post) return;

    // Title
    document.title = `${post.metaTitle} | Yacht Rock Detroit`;

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", post.metaDescription);

    // Keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.setAttribute("name", "keywords");
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute("content", post.keywords.join(", "));

    // OG tags
    const postUrl = `https://yachtrockdetroit.com/lifestyle/blog/${post.slug}`;
    const setOgTag = (prop: string, val: string) => {
      let el = document.querySelector(`meta[property="${prop}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };
    setOgTag("og:title", post.metaTitle);
    setOgTag("og:description", post.metaDescription);
    setOgTag("og:image", post.image);
    setOgTag("og:type", "article");
    setOgTag("og:url", postUrl);

    // Twitter
    const twImage = document.querySelector('meta[name="twitter:image"]');
    if (twImage) twImage.setAttribute("content", post.image);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://yachtrockdetroit.com/lifestyle/blog/${post.slug}`);

    // JSON-LD structured data
    let jsonLdScript = document.querySelector('script[data-blog-jsonld]') as HTMLScriptElement;
    if (!jsonLdScript) {
      jsonLdScript = document.createElement("script");
      jsonLdScript.setAttribute("type", "application/ld+json");
      jsonLdScript.setAttribute("data-blog-jsonld", "true");
      document.head.appendChild(jsonLdScript);
    }
    jsonLdScript.textContent = JSON.stringify(generateJsonLd(post));

    // BreadcrumbList JSON-LD
    const breadcrumbJsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://yachtrockdetroit.com/" },
        { "@type": "ListItem", "position": 2, "name": "Lifestyle", "item": "https://yachtrockdetroit.com/lifestyle" },
        { "@type": "ListItem", "position": 3, "name": "Blog", "item": "https://yachtrockdetroit.com/lifestyle/blog" },
        { "@type": "ListItem", "position": 4, "name": post.title, "item": postUrl },
      ],
    };
    let breadcrumbScript = document.querySelector('script[data-breadcrumb-seo="true"]') as HTMLScriptElement;
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement("script");
      breadcrumbScript.setAttribute("type", "application/ld+json");
      breadcrumbScript.setAttribute("data-breadcrumb-seo", "true");
      document.head.appendChild(breadcrumbScript);
    }
    breadcrumbScript.textContent = JSON.stringify(breadcrumbJsonLd);

    // Cleanup on unmount
    return () => {
      document.title = "Yacht Rock Detroit";
      if (metaDesc) metaDesc.setAttribute("content", "Detroit's smoothest summer festival. July 17-18, 2026.");
      const kwMeta = document.querySelector('meta[name="keywords"]');
      if (kwMeta) kwMeta.remove();
      const canonicalEl = document.querySelector('link[rel="canonical"]');
      if (canonicalEl) canonicalEl.remove();
      const jsonLdEl = document.querySelector('script[data-blog-jsonld]');
      if (jsonLdEl) jsonLdEl.remove();
      const bcEl = document.querySelector('script[data-breadcrumb-seo="true"]');
      if (bcEl) bcEl.remove();
      setOgTag("og:title", "Yacht Rock Detroit");
      setOgTag("og:description", "Detroit's smoothest summer festival. July 17-18, 2026.");
      setOgTag("og:type", "website");
      setOgTag("og:url", "https://yachtrockdetroit.com/");
    };
  }, [post]);

  if (!post) return <Navigate to="/lifestyle/blog" replace />;

  const ContentComponent = slug ? CONTENT_MAP[slug] : undefined;

  return (
    <main className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={heroRef} />

      {/* Hero Image */}
      <section ref={heroRef} className="relative h-[50vh] md:h-[60vh]">
        <img
          src={post.image}
          alt={post.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-yacht-charcoal/80 via-yacht-charcoal/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center bg-white text-yacht-charcoal text-[10px] font-semibold uppercase tracking-wider rounded-full px-3 py-1">
                {post.category}
              </span>
              <span className="text-xs text-white/70">{post.readTime}</span>
              <time className="text-xs text-white/70" dateTime={post.datePublished}>
                {new Date(post.datePublished).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </time>
            </div>
            <h1 className="font-serif text-3xl md:text-5xl text-white leading-tight">
              {post.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="py-16 md:py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <nav aria-label="Breadcrumb" className="mb-10">
            <ol className="flex items-center gap-2 text-sm text-muted-foreground">
              <li><Link to="/" className="hover:text-yacht-charcoal transition-colors">Home</Link></li>
              <li>/</li>
              <li><Link to="/lifestyle" className="hover:text-yacht-charcoal transition-colors">Lifestyle</Link></li>
              <li>/</li>
              <li><Link to="/lifestyle/blog" className="hover:text-yacht-charcoal transition-colors">Blog</Link></li>
              <li>/</li>
              <li className="text-yacht-charcoal truncate max-w-[200px]">{post.title}</li>
            </ol>
          </nav>

          {ContentComponent ? (
            <Suspense fallback={<div className="animate-pulse h-96 bg-yacht-charcoal/5 rounded-xl" />}>
              <ContentComponent />
            </Suspense>
          ) : (
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-muted-foreground italic">
                Content coming soon. Check back for the full article.
              </p>
            </div>
          )}
        </div>
      </article>

      <Footer />
    </main>
  );
};

export default BlogPost;
